import { useContext } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LanguageContext } from "../App";

const POLICY = {
  it: {
    title: "Privacy Policy",
    lastUpdate: "Ultimo aggiornamento: maggio 2025",
    sections: [
      {
        title: "1. Titolare del Trattamento",
        content: `Hotel Aurora Duino
Frazione Duino 15, 34011 Duino Aurisina (TS), Italia
P.IVA / C.F.: 01061080329
Email: info@auroraduino.com
Telefono: +39 040 208 212`,
      },
      {
        title: "2. Dati Personali Raccolti",
        content: `Raccogliamo le seguenti categorie di dati:

• Dati di navigazione: indirizzo IP, tipo di browser, pagine visitate, orari di accesso — raccolti automaticamente dal server e da sistemi di analisi.
• Dati forniti volontariamente: nome, email, numero di telefono comunicati tramite il modulo di contatto o tramite WhatsApp.
• Dati di prenotazione: gestiti direttamente da SimpleBooking.it (vedi sezione "Terze Parti"). Noi non archiviamo dati di pagamento.
• Cookie: dati di sessione e preferenze dell'utente (vedi sezione "Cookie").`,
      },
      {
        title: "3. Finalità e Base Giuridica del Trattamento",
        content: `I vostri dati sono trattati per le seguenti finalità:

• Gestione del sito web e risposta alle richieste di contatto — Base giuridica: legittimo interesse (Art. 6(1)(f) GDPR).
• Gestione delle prenotazioni — Base giuridica: esecuzione del contratto (Art. 6(1)(b) GDPR).
• Analisi statistica anonima delle visite — Base giuridica: consenso (Art. 6(1)(a) GDPR).
• Adempimento obblighi fiscali e legali — Base giuridica: obbligo legale (Art. 6(1)(c) GDPR).`,
      },
      {
        title: "4. Cookie",
        content: `Questo sito utilizza:

Cookie tecnici (essenziali): necessari per il funzionamento del sito. Non richiedono consenso.
• Cookie di sessione: mantengono le preferenze durante la navigazione.
• Cookie di lingua: memorizzano la lingua selezionata.

Cookie analitici (previo consenso):
• PostHog Analytics: raccoglie dati anonimi sulle visite per migliorare il sito.

Puoi gestire o revocare il consenso in qualsiasi momento tramite il banner cookie presente nel sito. Il rifiuto dei cookie analitici non pregiudica la navigazione.`,
      },
      {
        title: "5. Condivisione con Terze Parti",
        content: `I dati possono essere condivisi con:

• SimpleBooking.it (Kigo — Leofusion): sistema di prenotazione online. Consulta la loro privacy policy su simplebooking.it.
• PostHog Inc. (Stati Uniti): servizio di analisi web. I dati sono pseudonimizzati. PostHog è certificato con le garanzie adeguate ai sensi del GDPR.
• Autorità competenti: solo in caso di obbligo di legge.

Non vendiamo né cediamo i vostri dati personali a terzi per finalità commerciali.`,
      },
      {
        title: "6. Conservazione dei Dati",
        content: `• Dati di navigazione: 6 mesi.
• Dati di contatto: 12 mesi dalla data dell'ultimo contatto.
• Dati di prenotazione: 10 anni (obbligo fiscale ai sensi del D.Lgs. 127/1991).
• Cookie analitici: max 12 mesi.`,
      },
      {
        title: "7. Diritti dell'Interessato",
        content: `In qualità di interessato, hai il diritto di:

• Accedere ai tuoi dati personali (Art. 15 GDPR)
• Rettificare dati inesatti (Art. 16 GDPR)
• Richiedere la cancellazione ("diritto all'oblio") (Art. 17 GDPR)
• Limitare il trattamento (Art. 18 GDPR)
• Portabilità dei dati (Art. 20 GDPR)
• Opporti al trattamento (Art. 21 GDPR)
• Revocare il consenso in qualsiasi momento

Per esercitare questi diritti, contattaci a: info@auroraduino.com`,
      },
      {
        title: "8. Reclami",
        content: `Hai il diritto di proporre reclamo al Garante per la Protezione dei Dati Personali (www.garanteprivacy.it) se ritieni che il trattamento dei tuoi dati violi il GDPR.`,
      },
      {
        title: "9. Aggiornamenti",
        content: `Questa Privacy Policy può essere aggiornata periodicamente. La data di ultima revisione è indicata in cima alla pagina. Ti invitiamo a consultarla regolarmente.`,
      },
    ],
  },
  en: {
    title: "Privacy Policy",
    lastUpdate: "Last updated: May 2025",
    sections: [
      {
        title: "1. Data Controller",
        content: `Hotel Aurora Duino
Frazione Duino 15, 34011 Duino Aurisina (TS), Italy
VAT / Tax ID: 01061080329
Email: info@auroraduino.com
Phone: +39 040 208 212`,
      },
      {
        title: "2. Personal Data Collected",
        content: `We collect the following categories of data:

• Navigation data: IP address, browser type, pages visited, access times — collected automatically by the server and analytics systems.
• Voluntarily provided data: name, email, phone number submitted via the contact form or WhatsApp.
• Booking data: managed directly by SimpleBooking.it (see "Third Parties"). We do not store payment data.
• Cookies: session data and user preferences (see "Cookies" section).`,
      },
      {
        title: "3. Purposes and Legal Basis",
        content: `Your data is processed for the following purposes:

• Website management and responding to contact requests — Legal basis: legitimate interest (Art. 6(1)(f) GDPR).
• Booking management — Legal basis: performance of a contract (Art. 6(1)(b) GDPR).
• Anonymous statistical analysis of visits — Legal basis: consent (Art. 6(1)(a) GDPR).
• Fulfillment of legal and fiscal obligations — Legal basis: legal obligation (Art. 6(1)(c) GDPR).`,
      },
      {
        title: "4. Cookies",
        content: `This site uses:

Essential (technical) cookies: necessary for the website to function. No consent required.
• Session cookies: maintain preferences during navigation.
• Language cookies: store the selected language.

Analytical cookies (with consent):
• PostHog Analytics: collects anonymous visit data to improve the site.

You can manage or withdraw consent at any time via the cookie banner. Refusing analytical cookies does not affect your browsing experience.`,
      },
      {
        title: "5. Sharing with Third Parties",
        content: `Data may be shared with:

• SimpleBooking.it (Kigo — Leofusion): online booking system. See their privacy policy at simplebooking.it.
• PostHog Inc. (United States): web analytics service. Data is pseudonymized. PostHog maintains adequate GDPR safeguards.
• Competent authorities: only when required by law.

We do not sell or share your personal data with third parties for commercial purposes.`,
      },
      {
        title: "6. Data Retention",
        content: `• Navigation data: 6 months.
• Contact data: 12 months from the last contact.
• Booking data: 10 years (fiscal obligation).
• Analytical cookies: max 12 months.`,
      },
      {
        title: "7. Your Rights",
        content: `As a data subject, you have the right to:

• Access your personal data (Art. 15 GDPR)
• Rectify inaccurate data (Art. 16 GDPR)
• Request erasure ("right to be forgotten") (Art. 17 GDPR)
• Restrict processing (Art. 18 GDPR)
• Data portability (Art. 20 GDPR)
• Object to processing (Art. 21 GDPR)
• Withdraw consent at any time

To exercise these rights, contact us at: info@auroraduino.com`,
      },
      {
        title: "8. Complaints",
        content: `You have the right to lodge a complaint with the Italian Data Protection Authority (Garante — www.garanteprivacy.it) if you believe data processing violates the GDPR.`,
      },
      {
        title: "9. Updates",
        content: `This Privacy Policy may be updated periodically. The date of last revision is shown at the top of the page. We invite you to check it regularly.`,
      },
    ],
  },
  de: {
    title: "Datenschutzerklärung",
    lastUpdate: "Letzte Aktualisierung: Mai 2025",
    sections: [
      {
        title: "1. Verantwortlicher",
        content: `Hotel Aurora Duino
Frazione Duino 15, 34011 Duino Aurisina (TS), Italien
MwSt.-Nr. / CF: 01061080329
E-Mail: info@auroraduino.com
Telefon: +39 040 208 212`,
      },
      {
        title: "2. Erhobene Personendaten",
        content: `Wir erheben folgende Datenkategorien:

• Navigationsdaten: IP-Adresse, Browsertyp, besuchte Seiten, Zugriffszeiten — automatisch vom Server und Analysesystemen erfasst.
• Freiwillig übermittelte Daten: Name, E-Mail, Telefonnummer über das Kontaktformular oder WhatsApp.
• Buchungsdaten: direkt von SimpleBooking.it verwaltet (siehe "Dritte"). Wir speichern keine Zahlungsdaten.
• Cookies: Sitzungsdaten und Nutzerpräferenzen (siehe "Cookies").`,
      },
      {
        title: "3. Zwecke und Rechtsgrundlagen",
        content: `Ihre Daten werden für folgende Zwecke verarbeitet:

• Websitebetrieb und Beantwortung von Kontaktanfragen — Rechtsgrundlage: berechtigtes Interesse (Art. 6(1)(f) DSGVO).
• Buchungsverwaltung — Rechtsgrundlage: Vertragserfüllung (Art. 6(1)(b) DSGVO).
• Anonyme statistische Analyse — Rechtsgrundlage: Einwilligung (Art. 6(1)(a) DSGVO).
• Erfüllung rechtlicher und steuerlicher Pflichten — Rechtsgrundlage: rechtliche Verpflichtung (Art. 6(1)(c) DSGVO).`,
      },
      {
        title: "4. Cookies",
        content: `Diese Website verwendet:

Technische Cookies (notwendig): für den Betrieb der Website erforderlich. Keine Einwilligung erforderlich.
• Sitzungs-Cookies: behalten Einstellungen während der Navigation.
• Sprach-Cookies: speichern die gewählte Sprache.

Analyse-Cookies (mit Einwilligung):
• PostHog Analytics: erfasst anonyme Besuchsdaten zur Verbesserung der Website.

Die Einwilligung kann jederzeit über das Cookie-Banner widerrufen werden.`,
      },
      {
        title: "5. Weitergabe an Dritte",
        content: `Daten können weitergegeben werden an:

• SimpleBooking.it (Kigo — Leofusion): Online-Buchungssystem.
• PostHog Inc. (USA): Web-Analysedienst. Daten werden pseudonymisiert. PostHog erfüllt angemessene DSGVO-Garantien.
• Zuständige Behörden: nur bei gesetzlicher Verpflichtung.

Wir verkaufen Ihre Daten nicht an Dritte.`,
      },
      {
        title: "6. Datenspeicherung",
        content: `• Navigationsdaten: 6 Monate.
• Kontaktdaten: 12 Monate ab letztem Kontakt.
• Buchungsdaten: 10 Jahre (steuerliche Pflicht).
• Analyse-Cookies: max. 12 Monate.`,
      },
      {
        title: "7. Ihre Rechte",
        content: `Als betroffene Person haben Sie das Recht auf:

• Auskunft (Art. 15 DSGVO)
• Berichtigung (Art. 16 DSGVO)
• Löschung ("Recht auf Vergessenwerden") (Art. 17 DSGVO)
• Einschränkung der Verarbeitung (Art. 18 DSGVO)
• Datenübertragbarkeit (Art. 20 DSGVO)
• Widerspruch (Art. 21 DSGVO)
• Widerruf der Einwilligung jederzeit

Bitte kontaktieren Sie uns: info@auroraduino.com`,
      },
      {
        title: "8. Beschwerden",
        content: `Sie haben das Recht, eine Beschwerde beim Garante (www.garanteprivacy.it) einzureichen, wenn Sie der Ansicht sind, dass die Verarbeitung Ihrer Daten gegen die DSGVO verstößt.`,
      },
      {
        title: "9. Aktualisierungen",
        content: `Diese Datenschutzerklärung kann regelmäßig aktualisiert werden. Das Datum der letzten Überarbeitung ist oben auf der Seite angegeben.`,
      },
    ],
  },
  nl: {
    title: "Privacybeleid",
    lastUpdate: "Laatste update: mei 2025",
    sections: [
      { title: "1. Verantwoordelijke voor de verwerking", content: `Hotel Aurora Duino\nFrazione Duino 15, 34011 Duino Aurisina (TS), Italië\nBTW/CF: 01061080329\nE-mail: info@auroraduino.com\nTelefoon: +39 040 208 212` },
      { title: "2. Verzamelde persoonsgegevens", content: `Wij verzamelen:\n\n• Navigatiegegevens: IP-adres, browser, bezochte pagina's.\n• Vrijwillig verstrekte gegevens: naam, e-mail, telefoon via contactformulier of WhatsApp.\n• Reserveringsgegevens: beheerd door SimpleBooking.it. Wij slaan geen betalingsgegevens op.\n• Cookies: sessiegegevens en gebruikersvoorkeuren.` },
      { title: "3. Doeleinden en rechtsgrondslag", content: `• Websitebeheer en beantwoorden van vragen — Gerechtvaardigd belang (Art. 6(1)(f) AVG).\n• Reserveringsbeheer — Uitvoering van overeenkomst (Art. 6(1)(b) AVG).\n• Anonieme statistische analyse — Toestemming (Art. 6(1)(a) AVG).\n• Wettelijke verplichtingen — Wettelijke verplichting (Art. 6(1)(c) AVG).` },
      { title: "4. Cookies", content: `Technische cookies (essentieel): noodzakelijk voor het functioneren van de website.\nAnalytische cookies (met toestemming): PostHog Analytics.\n\nJe kunt de toestemming op elk moment beheren via de cookiebanner.` },
      { title: "5. Delen met derden", content: `• SimpleBooking.it: reserveringssysteem.\n• PostHog Inc. (VS): gepseudonimiseerde webanalyse.\n• Bevoegde autoriteiten: alleen indien wettelijk vereist.\n\nWij verkopen jouw gegevens niet aan derden.` },
      { title: "6. Bewaartermijnen", content: `• Navigatiegegevens: 6 maanden.\n• Contactgegevens: 12 maanden.\n• Reserveringsgegevens: 10 jaar (fiscale verplichting).\n• Analytische cookies: max. 12 maanden.` },
      { title: "7. Jouw rechten", content: `Je hebt recht op: inzage, rectificatie, verwijdering, beperking, overdraagbaarheid en bezwaar (Art. 15-21 AVG). Neem contact op: info@auroraduino.com` },
      { title: "8. Klachten", content: `Je kunt een klacht indienen bij de Italiaanse toezichthouder (Garante — www.garanteprivacy.it) als je van mening bent dat de verwerking de AVG schendt.` },
      { title: "9. Updates", content: `Dit privacybeleid kan periodiek worden bijgewerkt. De datum van de laatste revisie staat bovenaan de pagina.` },
    ],
  },
};

export default function PrivacyPolicyPage() {
  const { lang, t } = useContext(LanguageContext);
  const policy = POLICY[lang] || POLICY.en;

  return (
    <>
      {/* Simple header */}
      <div className="bg-[#2A2726] pt-28 pb-16 px-6">
        <div className="max-w-3xl mx-auto">
          <p className="font-['Manrope',sans-serif] text-xs uppercase tracking-[0.3em] text-[#7B907F] mb-4">
            Hotel Aurora Duino
          </p>
          <h1
            data-testid="privacy-title"
            className="font-['Cormorant_Garamond',serif] text-4xl md:text-5xl font-light text-[#FDFBF7] tracking-tight mb-3"
          >
            {policy.title}
          </h1>
          <p className="font-['Manrope',sans-serif] text-xs text-[#FDFBF7]/40">
            {policy.lastUpdate}
          </p>
        </div>
      </div>

      {/* Content */}
      <section className="bg-[#FDFBF7] py-16 md:py-24 px-6" data-testid="privacy-content">
        <div className="max-w-3xl mx-auto">
          <div className="flex flex-col gap-10">
            {policy.sections.map((section, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.05 }}
                className="border-b border-[#DDD8CE] pb-10 last:border-0"
                data-testid={`privacy-section-${i}`}
              >
                <h2 className="font-['Cormorant_Garamond',serif] text-2xl md:text-3xl font-light text-[#2A2726] mb-4">
                  {section.title}
                </h2>
                <div className="font-['Manrope',sans-serif] text-sm text-[#5C5754] leading-relaxed whitespace-pre-line">
                  {section.content}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Back link */}
          <div className="mt-14 pt-8 border-t border-[#DDD8CE]">
            <Link
              to="/"
              data-testid="privacy-back-link"
              className="font-['Manrope',sans-serif] text-xs uppercase tracking-[0.2em] text-[#7B907F] hover:opacity-70 transition-opacity border-b border-[#7B907F] pb-0.5"
            >
              ← {lang === "it" ? "Torna alla home" : lang === "de" ? "Zurück zur Startseite" : "Back to home"}
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
